import { motion } from "framer-motion";

const NODES = [
  { x: 30, y: 40 }, { x: 70, y: 30 }, { x: 50, y: 65 },
  { x: 20, y: 60 }, { x: 80, y: 62 }, { x: 60, y: 20 }, { x: 42, y: 48 },
];

function HoloGlobe() {
  return (
    <div className="relative w-full aspect-square max-w-[520px] mx-auto">
      {/* pulse rings */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute inset-0 rounded-full border border-[#22D3EE]/30"
          style={{ animation: `pulse-ring 3.5s ease-out ${i * 1.1}s infinite` }}
        />
      ))}

      <div className="absolute inset-[6%] rounded-full bg-[#22D3EE]/5 blur-2xl" />

      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-spin-slow drop-glow">
        <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(34,211,238,0.35)" strokeWidth="0.4" />
        {/* longitude */}
        {[0.25, 0.55, 0.85, 1.15, 1.45].map((rx, i) => (
          <ellipse key={i} cx="50" cy="50" rx={46 * rx > 46 ? 46 : 46} ry={46 * (0.2 + i * 0.2)} fill="none" stroke="rgba(34,211,238,0.15)" strokeWidth="0.3" />
        ))}
        {/* latitude */}
        {[14, 26, 38].map((ry, i) => (
          <ellipse key={i} cx="50" cy="50" rx="46" ry={ry} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" />
        ))}
      </svg>

      {/* nodes */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        {NODES.map((n, i) =>
          NODES.slice(i + 1).map((m, j) => (
            <line
              key={`${i}-${j}`}
              x1={n.x} y1={n.y} x2={m.x} y2={m.y}
              stroke="rgba(34,211,238,0.18)"
              strokeWidth="0.25"
            />
          ))
        )}
        {NODES.map((n, i) => (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r="1.4" fill="#22D3EE">
              <animate attributeName="opacity" values="0.4;1;0.4" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={n.x} cy={n.y} r="3" fill="none" stroke="#22D3EE" strokeWidth="0.3" opacity="0.5" />
          </g>
        ))}
      </svg>
    </div>
  );
}

const FACTS = [
  ["312", "Edge locations"],
  ["99.99%", "Uptime SLA"],
  ["11ms", "Avg. latency"],
];

export default function NetworkGlobe() {
  return (
    <section id="network" className="relative py-28 lg:py-40 bg-[#07070E] border-y border-white/10 overflow-hidden" data-testid="network-section">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Global backbone</span>
          <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none">
            A network that<br />wraps the world
          </h2>
          <p className="mt-8 max-w-lg text-base text-slate-400 leading-relaxed">
            As a reverse proxy between users and origin servers, Endasphere routes every
            request through the nearest node — filtering threats, caching content and
            shaving milliseconds off every connection, everywhere on Earth.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            {FACTS.map(([v, l]) => (
              <div key={l} data-testid={`network-fact-${l}`}>
                <div className="font-display font-700 text-2xl lg:text-3xl text-[#22D3EE]">{v}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.15em] text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <HoloGlobe />
        </motion.div>
      </div>
    </section>
  );
}
